import { isMockServices } from 'src/utils';
import { MutationFunction } from '@tanstack/react-query';
import {
  LoanChecklist,
  LoanChecklistMetadata,
  LoanChecklistRule,
} from '../loans/types';
import mock from '../utils/mock';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface SaveChecklistParams {
  checklistId: string | null;
  globalChecklistId: string | null;
  name: string;
  metadata: LoanChecklistMetadata;
  loanId: string;
  rules: LoanChecklistRule[];
  active: boolean;
}

interface SaveChecklistResponse {
  loanChecklistRule: LoanChecklist & { loanId: string };
  success: boolean;
}

const saveChecklist: MutationFunction<
  LoanChecklist & { loanId: string },
  SaveChecklistParams
> = async (args) => {
  if (isMockServices()) {
    return await mock(_mockSaveChecklist(args), {
      delay: 2000,
      failureRate: 0,
    });
  }

  const getActive: (active: boolean | null | undefined) => boolean = (
    active,
  ) => {
    return active === null || active === undefined ? true : active;
  };

  const url =
    args.checklistId == null
      ? `/loan-checklist-rules`
      : `/loan-checklist-rules/${args.checklistId}`;
  const method = args.checklistId == null ? 'POST' : 'PUT';
  // Keep the id on rules that already have one. It records the library rule the
  // entry was copied from, and checklist sync matches loan copies on it --
  // dropping it here makes sync append a duplicate `{name}-global` instead of
  // updating the rule in place.
  const toRuleBody = (rule: LoanChecklistRule) => ({
    ...(rule.id ? { id: rule.id } : {}),
    name: rule.name,
    description: rule.description,
    definition: rule.definition,
  });

  const body =
    args.checklistId == null
      ? {
          loanId: args.loanId,
          name: args.name,
          active: getActive(args.active),
          metadata: args.metadata,
          rules: args.rules.map(toRuleBody),
        }
      : {
          name: args.name,
          active: getActive(args.active),
          metadata: args.metadata,
          rules: args.rules.map(toRuleBody),
        };

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<
    SaveChecklistResponse & { checklistId: string }
  >(url, {
    method: method,
    body: body,
  });

  if (response.data) {
    return {
      loanId: args.loanId,
      id: response.data.loanChecklistRule.id,
      name: response.data.loanChecklistRule.name,
      metadata: response.data.loanChecklistRule.metadata,
      active: response.data.loanChecklistRule.active,
      rules: response.data.loanChecklistRule.rules,
    } as LoanChecklist & { loanId: string };
  }
  throw new Error('Failed to save checklist');
};

export default saveChecklist;

const _mockSaveChecklist = (checklist: SaveChecklistParams) => {
  return {
    loanId: checklist.loanId,
    id: checklist.checklistId ?? crypto.randomUUID(),
    name: checklist.name,
    metadata: checklist.metadata,
    active: true,
    rules: checklist.rules,
  } as LoanChecklist & { loanId: string };
};
